'use client';

import { useEffect, useRef, useState } from 'react';
import styles from './BeachBackground.module.scss';

interface BeachBackgroundProps {
  quality?: 'low' | 'medium' | 'high';
  enableAnimations?: boolean;
}

interface WaveConfig {
  colorFront: string;
  colorBack: string;
  amplitude: [number, number];
  frequency: [number, number];
  speed: [number, number];
  height: number;
  direction: number;
}

export default function BeachBackground({ 
  quality = 'high', 
  enableAnimations = true 
}: BeachBackgroundProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const sunRef = useRef<HTMLDivElement>(null);
  const moonRef = useRef<HTMLDivElement>(null);
  const boatRef = useRef<HTMLDivElement>(null);
  const [isClient, setIsClient] = useState(false);
  const [_animationsLoaded, setAnimationsLoaded] = useState(false);
  const [timeOfDay, setTimeOfDay] = useState<'dawn' | 'day' | 'dusk' | 'night'>('dawn');
  const animationFrameRef = useRef<number | undefined>(undefined);
  const timeRef = useRef({ front: 0, back: 0, boat: 0 });

  // Wave configuration - using useRef to avoid ESLint warnings
  const waveConfigRef = useRef<WaveConfig>({
    colorFront: 'rgba(102, 217, 239, 0.8)', // Turquoise
    colorBack: 'rgba(0, 153, 204, 0.4)', // Ocean blue
    amplitude: [12, 15], // 波の高さを小さくする
    frequency: [0.015, 0.01], // Wave frequency
    speed: [0.5, 0.8], // Wave speed
    height: 100, // 波の高さを小さくする
    direction: 1 // Right to left
  });
  const waveConfig = waveConfigRef.current;

  // Ensure client-side only rendering
  useEffect(() => {
    setIsClient(true);
  }, []);

  // Canvas wave animation
  useEffect(() => {
    if (!isClient || !canvasRef.current || !enableAnimations) {return;}

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) {return;}

    const resize = () => {
      canvas.width = window.innerWidth;
      // 波の高さをウィンドウサイズに対する比率に
      canvas.height = Math.round(window.innerHeight * 0.08); // ウィンドウ高さの8%
      waveConfig.height = canvas.height;
    };

    window.addEventListener('resize', resize);
    resize();

    const drawWaveLayer = (
      freq: number,
      amp: number,
      time: number,
      direction: number,
      yOffset: number = 0
    ) => {
      ctx.beginPath();
      ctx.moveTo(0, canvas.height);

      // 振幅もウィンドウサイズに対する比率に
      const scaledAmp = amp * (window.innerHeight / 1000); // 基準高さ1000pxに対する比率

      for (let x = 0; x <= canvas.width; x += 1) {
        const phase = (x * freq) - (direction * time * freq);
        const y = canvas.height / 2 + Math.sin(phase) * scaledAmp + yOffset;
        ctx.lineTo(x, y);
      }

      ctx.lineTo(canvas.width, canvas.height + 10);
      ctx.lineTo(0, canvas.height + 10);
      ctx.closePath();
      
      // 海の色と同じグラデーションを適用（下に向かって徐々に透明に）
      const gradient = ctx.createLinearGradient(0, 0, 0, canvas.height + 10);
      if (timeOfDay === 'dawn') {
        gradient.addColorStop(0, 'rgba(70, 130, 180, 0.7)'); // Steel blue
        gradient.addColorStop(0.7, 'rgba(70, 130, 180, 0.7)');
        gradient.addColorStop(1, 'rgba(70, 130, 180, 0)');
      } else if (timeOfDay === 'day') {
        gradient.addColorStop(0, 'rgba(102, 217, 239, 0.7)'); // Turquoise
        gradient.addColorStop(0.7, 'rgba(0, 153, 204, 0.7)'); // Ocean blue
        gradient.addColorStop(1, 'rgba(0, 153, 204, 0)');
      } else if (timeOfDay === 'dusk') {
        gradient.addColorStop(0, 'rgba(72, 61, 139, 0.7)'); // Dark slate blue
        gradient.addColorStop(0.7, 'rgba(72, 61, 139, 0.7)');
        gradient.addColorStop(1, 'rgba(72, 61, 139, 0)');
      } else {
        // Night mode - match the ocean gradient colors
        gradient.addColorStop(0, 'rgba(20, 52, 82, 0.7)'); // darken($ocean-shallow, 30%) ≈ #143452
        gradient.addColorStop(0.7, 'rgba(10, 35, 60, 0.7)'); // darken($ocean-mid, 35%) ≈ #0a233c
        gradient.addColorStop(1, 'rgba(0, 20, 40, 0)');
      }
      
      ctx.fillStyle = gradient;
      ctx.fill();
    };

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Draw back wave
      drawWaveLayer(
        waveConfig.frequency[1],
        waveConfig.amplitude[1],
        timeRef.current.back,
        waveConfig.direction,
        -5
      );

      // Draw front wave
      drawWaveLayer(
        waveConfig.frequency[0],
        waveConfig.amplitude[0],
        timeRef.current.front,
        waveConfig.direction,
        0
      );

      timeRef.current.front += waveConfig.speed[0];
      timeRef.current.back += waveConfig.speed[1];

      animationFrameRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', resize);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isClient, enableAnimations, timeOfDay]);

  // Sun/Moon and boat animations with synchronized sky colors
  useEffect(() => {
    if (!isClient || !enableAnimations) {return;}

    const cycleDuration = 60000; // 60 seconds for full day/night cycle
    const startTime = Date.now();

    const updateCelestialBodies = () => {
      const elapsed = Date.now() - startTime;
      const progress = (elapsed % cycleDuration) / cycleDuration;
      const isDaytime = progress < 0.5;
      
      // Update sky gradient based on celestial position
      if (isDaytime) {
        const sunProgress = progress * 2; // 0 to 1 during day
        if (sunProgress < 0.25) {
          setTimeOfDay('dawn'); // Dawn: sun in lower left
        } else if (sunProgress < 0.75) {
          setTimeOfDay('day'); // Day: sun overhead
        } else {
          setTimeOfDay('dusk'); // Dusk: sun in lower right
        }
      } else {
        const moonProgress = (progress - 0.5) * 2; // 0 to 1 during night
        if (moonProgress < 0.25) {
          setTimeOfDay('dusk'); // Early evening: moon rising
        } else if (moonProgress < 0.75) {
          setTimeOfDay('night'); // Night: moon overhead
        } else {
          setTimeOfDay('dawn'); // Pre-dawn: moon setting
        }
      }

      // Sun animation (0 to 0.5 of cycle) - from lower left to upper center to lower right
      if (sunRef.current) {
        if (isDaytime) {
          const sunProgress = progress * 2; // 0 to 1 during day
          
          // 明確な3点を定義（画面サイズに対する相対位置）
          const viewportWidth = window.innerWidth;
          const viewportHeight = window.innerHeight;
          
          // 始点：地平線の左下
          const startX = viewportWidth * 0.02; // 左端から2%（より端に）
          const startY = viewportHeight * 0.65; // 上から65%（地平線近く）
          
          // 中間点：中央上（モバイルでは調整）
          const isMobile = viewportWidth < 768;
          const midX = viewportWidth * (isMobile ? 0.40 : 0.5); // モバイルではさらに左に（0.45→0.40）
          const midY = viewportHeight * (isMobile ? 0.40 : 0.25); // モバイルではさらに低く（0.35→0.40）
          
          // 終点：モバイルでは下、デスクトップでは少し上
          const endX = viewportWidth * (isMobile ? 0.95 : 0.98); // モバイルでは95%位置
          const endY = viewportHeight * (isMobile ? 0.60 : 0.60); // モバイルでは下、デスクトップでは少し上
          
          let x = 0;
          let y = 0;
          
          // 上に凸の放物線で補間（全体を一つの曲線として）
          const t = sunProgress; // 0 to 1
          
          // 3点を通る2次ベジェ曲線
          if (sunProgress <= 0.5) {
            // 始点から中間点へ
            const localT = sunProgress * 2; // 0 to 1
            x = startX + (midX - startX) * localT;
            y = startY + (midY - startY) * localT;
          } else {
            // 中間点から終点へ
            const localT = (sunProgress - 0.5) * 2; // 0 to 1
            x = midX + (endX - midX) * localT;
            y = midY + (endY - midY) * localT;
          }
          
          // 全体に対する放物線の高さ補正
          const arcHeight = -Math.sin(t * Math.PI) * viewportHeight * 0.15;
          y += arcHeight;
          
          // サイズの変化（始点と終点で最大、中間点でも大きめに）
          // sunProgress: 0 → 0.5 → 1
          // distance from 0.5: 0.5 → 0 → 0.5
          // scale: 1.2 → 0.6 → 1.2 (モバイルでは中間点も大きく)
          const distanceFromCenter = Math.abs(sunProgress - 0.5);
          const baseSizeScale = isMobile ? 0.8 + (distanceFromCenter * 2 * 0.4) : 0.5 + (distanceFromCenter * 2 * 0.7); // モバイル: 0.8-1.2, デスクトップ: 0.5-1.2（中間点を大きく）
          const sizeScale = isMobile ? baseSizeScale * 1.5 : baseSizeScale; // モバイルでは1.5倍大きく
          
          // 画面中央を基準とした相対位置に変換
          const centerX = viewportWidth / 2;
          const centerY = viewportHeight / 2;
          
          sunRef.current.style.transform = `translate(${x - centerX}px, ${y - centerY}px) scale(${sizeScale})`;
          sunRef.current.style.opacity = '1';
        } else {
          sunRef.current.style.opacity = '0';
        }
      }

      // Moon animation (0.5 to 1 of cycle) - from lower left to upper center to lower right
      if (moonRef.current) {
        if (!isDaytime) {
          const moonProgress = (progress - 0.5) * 2; // 0 to 1 during night
          
          // 明確な3点を定義（画面サイズに対する相対位置）
          const viewportWidth = window.innerWidth;
          const viewportHeight = window.innerHeight;
          
          // 始点：地平線の左下
          const startX = viewportWidth * 0.02; // 左端から2%（より端に）
          const startY = viewportHeight * 0.65;
          
          // 中間点：中央上（モバイルでは調整）
          const isMobile = viewportWidth < 768;
          const midX = viewportWidth * (isMobile ? 0.40 : 0.5); // モバイルではさらに左に（0.45→0.40）
          const midY = viewportHeight * (isMobile ? 0.40 : 0.25); // モバイルではさらに低く（0.35→0.40）
          
          // 終点：モバイルでは下、デスクトップでは少し上
          const endX = viewportWidth * (isMobile ? 0.98 : 0.98); // モバイルでももっと右に
          const endY = viewportHeight * (isMobile ? 0.60 : 0.60); // モバイルでは下、デスクトップでは少し上
          
          let x = 0;
          let y = 0;
          
          // 太陽と同じ上に凸の放物線で補間（全体を一つの曲線として）
          const t = moonProgress; // 0 to 1
          
          // 3点を通る2次ベジェ曲線（太陽と同じロジック）
          if (moonProgress <= 0.5) {
            // 始点から中間点へ
            const localT = moonProgress * 2; // 0 to 1
            x = startX + (midX - startX) * localT;
            y = startY + (midY - startY) * localT;
          } else {
            // 中間点から終点へ
            const localT = (moonProgress - 0.5) * 2; // 0 to 1
            x = midX + (endX - midX) * localT;
            y = midY + (endY - midY) * localT;
          }
          
          // 全体に対する放物線の高さ補正（太陽と同じ）
          const arcHeight = -Math.sin(t * Math.PI) * viewportHeight * 0.15;
          y += arcHeight;
          
          // サイズの変化（太陽と同じロジック）
          const distanceFromCenter = Math.abs(moonProgress - 0.5);
          const baseSizeScale = isMobile ? 0.8 + (distanceFromCenter * 2 * 0.4) : 0.5 + (distanceFromCenter * 2 * 0.7);
          const sizeScale = isMobile ? baseSizeScale * 1.5 : baseSizeScale * 1.2; // 月をさらに大きく
          
          // 画面中央を基準とした相対位置に変換
          const centerX = viewportWidth / 2;
          const centerY = viewportHeight / 2;
          
          moonRef.current.style.transform = `translate(${x - centerX}px, ${y - centerY}px) scale(${sizeScale})`;
          moonRef.current.style.opacity = '1';
        } else {
          moonRef.current.style.opacity = '0';
        }
      }
    };

    // Boat bobbing and sailing animation
    const animateBoat = () => {
      if (boatRef.current) {
        // 30 seconds for full cycle at 60fps
        // 30s * 60fps = 1800 frames
        // Each frame increment: 1/1800
        timeRef.current.boat += 1 / 1800;
        
        // Vertical bobbing with sine wave (very subtle)
        const bobAmount = Math.sin(timeRef.current.boat * Math.PI * 2 * 4) * (window.innerHeight * 0.005); // 0.5% of window height, 4 gentle bobs
        
        // Tilting motion synchronized with bobbing
        const tiltAmount = Math.sin(timeRef.current.boat * Math.PI * 2 * 4 + Math.PI / 4) * 2; // Very subtle tilt
        
        // Horizontal sailing motion (30 second journey from -10% to 110%)
        const progress = timeRef.current.boat % 1;
        const xPosition = progress * 120 - 10; // Move from -10vw to 110vw
        
        // Reset position when boat reaches the end
        if (timeRef.current.boat >= 1) {
          timeRef.current.boat = 0;
        }
        
        // Boat scale based on window width
        const boatScale = Math.min(2.0, window.innerWidth / 600);
        
        // Apply all transformations
        boatRef.current.style.transform = `translateX(${xPosition}vw) translateY(${bobAmount}px) rotate(${tiltAmount}deg) scale(${boatScale})`;
      }
    };

    // 初期状態でも太陽・月の位置を即座に反映させる
    updateCelestialBodies();

    const animationInterval = setInterval(() => {
      updateCelestialBodies();
      animateBoat();
    }, 16); // ~60fps

    // Cloud animations with responsive sizing
    const animateClouds = async () => {
      try {
        const { gsap } = await import('gsap');
        
        const clouds = containerRef.current?.querySelectorAll(`.${styles.cloud}`);
        if (clouds) {
          clouds.forEach((cloud, index) => {
            const baseSpeed = window.innerWidth < 768 ? 80 : 60;
            gsap.to(cloud, {
              x: '+=150vw',
              duration: baseSpeed + index * 20,
              ease: 'none',
              repeat: -1,
              delay: index * 5
            });
          });
        }

        setAnimationsLoaded(true);
      } catch (error) {
        console.error('Failed to load animations:', error);
      }
    };

    if (quality !== 'low') {
      animateClouds();
    }

    return () => {
      clearInterval(animationInterval);
    };
  }, [isClient, enableAnimations, quality]);

  if (!isClient) {
    return null;
  }

  // Responsive sizing based on viewport
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  const scale = isMobile ? 0.7 : 1;

  return (
    <div 
      ref={containerRef} 
      className={`${styles.beachBackground} ${styles[timeOfDay]}`} 
      data-quality={quality}
    >
      {/* Sky gradient layer */}
      <div className={styles.sky}>
        <div className={styles.skyGradient} />
        
        {/* Sun */}
        <div ref={sunRef} className={styles.sunContainer} style={{ transition: 'opacity 2s' }}>
          <div className={styles.sun}>
            <div className={styles.sunCore} />
            <div className={styles.sunGlow} />
            {quality === 'high' && <div className={styles.sunRays} />}
          </div>
        </div>

        {/* Moon */}
        <div ref={moonRef} className={styles.moonContainer} style={{ transition: 'opacity 2s' }}>
          <div className={styles.moon}>
            <div className={styles.moonCore} />
            <div className={styles.moonGlow} />
            <div className={styles.moonCraters} />
          </div>
        </div>

        {/* Clouds - show on mobile with reduced count */}
        {quality !== 'low' && (
          <>
            <div className={`${styles.cloud} ${styles.cloud1}`} style={{ transform: `scale(${scale})` }} />
            <div className={`${styles.cloud} ${styles.cloud2}`} style={{ transform: `scale(${scale})` }} />
            {!isMobile && (
              <>
                <div className={`${styles.cloud} ${styles.cloud3}`} />
                {quality === 'high' && (
                  <>
                    <div className={`${styles.cloud} ${styles.cloud4}`} style={{ transform: `scale(${scale * 0.9})` }} />
                    <div className={`${styles.cloud} ${styles.cloud5}`} style={{ transform: `scale(${scale * 1.1})` }} />
                  </>
                )}
              </>
            )}
          </>
        )}
      </div>

      {/* Ocean with Canvas waves */}
      <div className={styles.ocean}>
        <canvas 
          ref={canvasRef} 
          className={styles.waveCanvas}
          style={{ 
            position: 'absolute',
            bottom: '50%', /* 砂浜と地平線の中間（海の真ん中あたり）に配置 */
            left: 0,
            width: '100%',
            height: `${waveConfig.height}px`,
            zIndex: 4 /* ボートより前面に配置 */
          }}
        />

        {/* Foam effects */}
        {quality === 'high' && !isMobile && (
          <div className={styles.foam}>
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className={styles.foamBubble} />
            ))}
          </div>
        )}
      </div>

      {/* Beach */}
      <div className={styles.beach}>
        <div className={styles.sandGradient} />
        {quality !== 'low' && (
          <>
            <div className={styles.sandTexture} />
            <div className={styles.shoreline} />
          </>
        )}
      </div>

      {/* Decorative elements - responsive */}
      {(quality !== 'low') && (
        <div className={styles.decorations}>
          {/* Birds - show on mobile with reduced count */}
          <div className={styles.birds}>
            <div className={styles.bird} style={{ transform: `scale(${scale})` }}>
              <span />
            </div>
            <div className={styles.bird} style={{ transform: `scale(${scale * 0.8})` }}>
              <span />
            </div>
            {!isMobile && quality === 'high' && (
              <>
                <div className={styles.bird} style={{ transform: `scale(${scale * 1.2})` }}>
                  <span />
                </div>
                <div className={styles.bird} style={{ transform: `scale(${scale * 0.9})` }}>
                  <span />
                </div>
                <div className={styles.bird} style={{ transform: `scale(${scale * 1.1})` }}>
                  <span />
                </div>
              </>
            )}
          </div>
          
          {/* Boat with bobbing animation - show on mobile */}
          {enableAnimations && (
            <div ref={boatRef} className={styles.boat} style={{ transformOrigin: 'center bottom' }}>
              <div className={styles.boatBody} />
              <div className={styles.boatSail} />
              <div className={styles.boatMast} />
              <div className={styles.boatFlag} />
            </div>
          )}
        </div>
      )}

      {/* Overlay effects */}
      <div className={styles.overlayEffects}>
        {!isMobile && quality !== 'low' && <div className={styles.lightRays} />}
        <div className={styles.atmosphericHaze} />
      </div>
    </div>
  );
}